/**
 * @fileoverview Job → Canonical Field Mapping
 *
 * Maps job data to canonical field keys for auto-populating precedent templates.
 * Three-tier merge: user edits > job data > org field defaults.
 */

import { titleCaseCompanyName } from "../../lib/companyName";
import { isPersonalInsolvencyAppointmentType } from "../../lib/appointmentTypes";
import { resolveBankruptPersonName } from "../../lib/bankruptName";
import {
  resolveScopedTitles,
  withoutTitleConfigKeys,
} from "../../lib/titleScopes";
import {
  additionalPlaceOfBusinessLines,
  type PlaceOfBusinessFields,
} from "../../lib/placesOfBusiness";
import { sydneyDateIso } from "../../lib/sydneyDate";
import {
  sumEmployeeEntitlements,
  sumFegRecoverable,
  type EmployeeEntitlementAmounts,
} from "../../lib/employeeEntitlements";
import { deriveNameParts, composeFullName } from "../../contacts/nameParts";
import { splitPractitionerName } from "./practitionerNameParts";
import { normaliseName } from "../../contacts/aggregator";
import {
  buildContactCustomFieldKey,
  buildContactFieldKey,
  buildContactSourceFieldKey,
  buildFirmRefCustomFieldKey,
  buildFirmRefFieldKey,
  CONTACT_FIELD_PARTS,
  type ContactFieldDomain,
  type FirmContactCategory,
  type FirmContactCustomFieldType,
  type ContactSourceKind,
} from "./contactFields";

type CompanyNameVariantType =
  "trading_name" | "trust_name" | "former_trading_name";

interface CompanyNameVariant {
  name: string;
  type: CompanyNameVariantType;
  includeInDocuments: boolean;
}

const AUSTRALIAN_STATE_CODES = [
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
] as const;

/**
 * MOR-340 — human-readable labels for the machine-readable trustee capacity
 * enum. Used when `trustee_capacity` is substituted into precedent templates
 * so the rendered document reads "Sole Trustee" rather than `sole_trustee`.
 */
const TRUSTEE_CAPACITY_LABELS: Record<
  "sole_trustee" | "dual_capacity" | "unknown",
  string
> = {
  sole_trustee: "Sole Trustee",
  dual_capacity: "Dual Capacity",
  unknown: "Unknown",
};

const ANZSIC_DIVISIONS: Array<{
  start: number;
  end: number;
  code: string;
  name: string;
}> = [
  { start: 1, end: 5, code: "A", name: "Agriculture, Forestry and Fishing" },
  { start: 6, end: 10, code: "B", name: "Mining" },
  { start: 11, end: 25, code: "C", name: "Manufacturing" },
  {
    start: 26,
    end: 26,
    code: "D",
    name: "Electricity, Gas, Water and Waste Services",
  },
  { start: 30, end: 32, code: "E", name: "Construction" },
  { start: 33, end: 33, code: "F", name: "Wholesale Trade" },
  { start: 39, end: 43, code: "G", name: "Retail Trade" },
  { start: 44, end: 45, code: "H", name: "Accommodation and Food Services" },
  { start: 46, end: 53, code: "I", name: "Transport, Postal and Warehousing" },
  {
    start: 54,
    end: 60,
    code: "J",
    name: "Information Media and Telecommunications",
  },
  { start: 62, end: 64, code: "K", name: "Financial and Insurance Services" },
  {
    start: 66,
    end: 67,
    code: "L",
    name: "Rental, Hiring and Real Estate Services",
  },
  {
    start: 69,
    end: 70,
    code: "M",
    name: "Professional, Scientific and Technical Services",
  },
  {
    start: 72,
    end: 73,
    code: "N",
    name: "Administrative and Support Services",
  },
  { start: 75, end: 75, code: "O", name: "Public Administration and Safety" },
  { start: 80, end: 82, code: "P", name: "Education and Training" },
  { start: 84, end: 87, code: "Q", name: "Health Care and Social Assistance" },
  { start: 89, end: 92, code: "R", name: "Arts and Recreation Services" },
  { start: 94, end: 96, code: "S", name: "Other Services" },
];

// ---------------------------------------------------------------------------
// Appointment type → human-readable labels
// ---------------------------------------------------------------------------

export const APPOINTMENT_TYPE_TO_ADMIN_TYPE: Record<string, string> = {
  cvl: "Liquidator",
  mvl: "Liquidator",
  simplified_liquidation: "Liquidator",
  court_liquidation: "Liquidator",
  provisional_liquidation: "Provisional Liquidator",
  sbr: "Small Business Restructuring Practitioner",
  voluntary_administration: "Voluntary Administrator",
  deed_of_company_arrangement: "Deed Administrator",
  receivership: "Receiver",
  scheme_of_arrangement: "Scheme Administrator",
  personal_bankruptcy: "Bankruptcy Trustee",
  business_bankruptcy: "Bankruptcy Trustee",
  creditors_petition: "Bankruptcy Trustee",
  debtors_petition: "Bankruptcy Trustee",
  // MOR-1838 — the trustee of a Part X personal insolvency agreement; the
  // debtor is not bankrupt, so not a "Bankruptcy" Trustee.
  part_x_arrangement: "Trustee",
};

export const APPOINTMENT_TYPE_TO_INSOLVENCY_TYPE: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation",
  mvl: "Members Voluntary Liquidation",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring",
  voluntary_administration: "Voluntary Administration",
  deed_of_company_arrangement: "Deed of Company Arrangement",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

const APPOINTMENT_TYPE_TO_METHOD: Record<string, string> = {
  cvl: "resolution of creditors",
  mvl: "resolution of members",
  simplified_liquidation: "resolution of creditors",
  court_liquidation: "appointment by court order",
  provisional_liquidation: "appointment by court order",
  voluntary_administration: "appointment by directors",
  receivership: "appointment by secured creditor",
  creditors_petition: "sequestration order on a creditor's petition",
  debtors_petition: "acceptance of a debtor's petition",
};

// ---------------------------------------------------------------------------
// Job data → canonical field map
// ---------------------------------------------------------------------------

interface JobData {
  /**
   * Firm's own matter reference (e.g. "J-0019"), minted at creation and
   * editable in Job Settings. Undefined on jobs created before job codes
   * existed — those simply don't emit the `job_code` field.
   */
  jobCode?: string | null;
  entityName: string;
  acn?: string | null;
  abn?: string | null;
  // MOR-340 trust handling — exposed as `trust_abn`, `trust_name`, and
  // `trustee_capacity` canonical fields on precedent output.
  trustAbn?: string | null;
  trustName?: string | null;
  trusteeCapacity?: "sole_trustee" | "dual_capacity" | "unknown" | null;
  registeredAddress?: string | null;
  registeredOfficeStreet?: string | null;
  registeredOfficeSuburb?: string | null;
  registeredOfficeState?: string | null;
  registeredOfficePostcode?: string | null;
  principalPlaceStreet?: string | null;
  principalPlaceSuburb?: string | null;
  principalPlaceState?: string | null;
  principalPlacePostcode?: string | null;
  /** MOR-989 — manually entered trading addresses (see `additional_place_of_business_lines`). */
  additionalPlacesOfBusiness?: PlaceOfBusinessFields[] | null;
  companyNameVariants?: CompanyNameVariant[] | null;
  anzsicCode?: string | null;
  /**
   * Optional for pre-appointment jobs (Flow A). Downstream mapping skips
   * appointment-derived fields when not present.
   */
  appointmentType?: string;
  appointmentDate?: string;
  practitioners: string[];
  appointees?: string[] | null;
  // MOR-429 day-one scalars — populated by NOA extraction or manual entry.
  // Plaintiff/defendant/bankrupt names are stored verbatim (case preserved).
  courtName?: string | null;
  courtState?: string | null;
  courtNumber?: string | null;
  sequestrationNumber?: string | null;
  proceedingMatterNumber?: string | null;
  proceedingYear?: string | null;
  courtOrderDate?: string | null;
  windingUpApplicationDate?: string | null;
  /** The s513C day — set where this appointment followed an administration. */
  administrationStartDate?: string | null;
  liquidationDate?: string | null;
  /**
   * Appointment history, most recent last (MOR-901). Exposes the appointment
   * this one succeeded to day-one precedents — a liquidator's first letter
   * routinely has to recite when the administration began.
   */
  priorAppointments?: Array<{
    appointmentType: string;
    appointmentDate?: string;
  }> | null;
  plaintiffName?: string | null;
  defendantName?: string | null;
  bankruptName?: string | null;
  bankruptDateOfBirth?: string | null;
  administrationNumber?: string | null;
  petitioningCreditorCosts?: string | null;
  actOfBankruptcyDate?: string | null;
  // MOR-770 — estate/administration bank account (Job Settings → Job Details).
  bankName?: string | null;
  bankAccountName?: string | null;
  bankBsb?: string | null;
  bankAccountNumber?: string | null;
  /**
   * Job overview "mini wiki" background summary (`jobs.overviewSummaryHtml`),
   * the AI-drafted, practitioner-editable background of the enterprise. Exposed
   * as the plain-text `background_summary` canonical field so day-one
   * precedents can drop it into a "background of the company" paragraph.
   */
  overviewSummaryHtml?: string | null;
}

/**
 * Maps job record fields to canonical field keys.
 * Returns a flat Record<string, string> of all fields derivable from the job.
 */
export function mapJobToCanonicalFields(job: JobData): Record<string, string> {
  const fields: Record<string, string> = {};

  // Firm's matter reference, for the "Our Ref" line on correspondence.
  if (job.jobCode) fields.job_code = job.jobCode;

  // Company details — title-case here as a safety net for legacy jobs
  // whose `entityName` is still ALL CAPS in the DB. Newer jobs already
  // store the cased value via the create-job dialog, so this is a no-op
  // for them. The user can still override `company_name` directly via
  // the Edit Fields panel if our heuristic mis-cases an initialism.
  if (job.entityName)
    fields.company_name = titleCaseCompanyName(job.entityName);
  // MOR-2342 — on a personal insolvency the subject is the individual, so the
  // name fields describe the *person*: `company_name` (relabelled "Bankrupt
  // Name" in the wizard) and the insurer's `appointment_name` carry the
  // bankrupt's name only. The practitioner's estate heading ("Bankrupt Estate
  // of …", typed into Job Settings) stays available as the explicit
  // `bankrupt_estate_name`; any "Bankrupt Estate of" wording in a letter is
  // the template's static text, never prepended to the value.
  const personal = isPersonalInsolvencyAppointmentType(job.appointmentType);
  const bankruptPersonName = personal
    ? resolveBankruptPersonName(job)
    : undefined;
  if (personal && job.entityName) {
    fields.bankrupt_estate_name = titleCaseCompanyName(job.entityName);
  }
  if (bankruptPersonName) fields.company_name = bankruptPersonName;
  if (fields.company_name) fields.appointment_name = fields.company_name;
  const includedNameVariants = (job.companyNameVariants ?? [])
    .filter((variant) => variant.includeInDocuments)
    .map((variant) => titleCaseCompanyName(variant.name.trim()))
    .filter(Boolean);
  // MOR-340 — also include the single trust name (if any) in the
  // aggregate line block. De-dupe case-insensitively so a manual variant
  // entry and a new `trustName` for the same trust collapse.
  if (job.trustName?.trim()) {
    const trustNameCased = titleCaseCompanyName(job.trustName.trim());
    if (
      !includedNameVariants.some(
        (n) => n.toLowerCase() === trustNameCased.toLowerCase(),
      )
    ) {
      includedNameVariants.push(trustNameCased);
    }
  }
  if (includedNameVariants.length > 0) {
    fields.company_name_variant_lines = includedNameVariants.join("\n");
  }
  const variantGroups: Record<CompanyNameVariantType, string[]> = {
    trading_name: [],
    trust_name: [],
    former_trading_name: [],
  };
  for (const variant of job.companyNameVariants ?? []) {
    if (!variant.includeInDocuments) continue;
    const name = titleCaseCompanyName(variant.name.trim());
    if (!name) continue;
    variantGroups[variant.type].push(name);
  }
  // MOR-340 — fold the single `trustName` field (captured during job
  // creation when the company acts as trustee) into `trust_name_lines` so
  // existing templates that use the multi-trust-name placeholder also pick
  // up the new field. De-dupe by case-insensitive match so a user who has
  // *both* a manual variant entry and a new trustName doesn't see the same
  // trust listed twice.
  if (job.trustName?.trim()) {
    const trustNameCased = titleCaseCompanyName(job.trustName.trim());
    const alreadyListed = variantGroups.trust_name.some(
      (n) => n.toLowerCase() === trustNameCased.toLowerCase(),
    );
    if (!alreadyListed) variantGroups.trust_name.push(trustNameCased);
  }
  if (variantGroups.trading_name.length > 0) {
    fields.trading_name_lines = variantGroups.trading_name.join("\n");
  }
  if (variantGroups.trust_name.length > 0) {
    fields.trust_name_lines = variantGroups.trust_name.join("\n");
  }
  if (variantGroups.former_trading_name.length > 0) {
    fields.former_trading_name_lines =
      variantGroups.former_trading_name.join("\n");
  }
  if (job.acn) fields.acn = job.acn;
  if (job.abn) fields.abn = job.abn;
  // MOR-340 — trust handling. `trust_abn` / `trust_name` expose the trust
  // entity separately from the company so precedent templates can render
  // the operating-business identifiers. `trustee_capacity` is a
  // machine-readable signal (sole_trustee | dual_capacity | unknown) that
  // templates and downstream agents can branch on.
  if (job.trustAbn) fields.trust_abn = job.trustAbn;
  if (job.trustName) fields.trust_name = job.trustName;
  if (job.trusteeCapacity) {
    fields.trustee_capacity = TRUSTEE_CAPACITY_LABELS[job.trusteeCapacity];
  }
  if (job.anzsicCode) {
    fields.anzsic_code = job.anzsicCode;
    const industryDivision = deriveAnzsicDivision(job.anzsicCode);
    if (industryDivision) {
      fields.industry_division = industryDivision;
      fields.business_industry = industryDivision;
    }
  }

  // Job overview background summary → plain-text `background_summary`. The
  // overview card stores HTML; precedents substitute plain text, so tags are
  // stripped and paragraphs become blank-line-separated blocks (same shape as
  // `job_bank_details`). Left unmapped when the summary is empty so the field
  // simply renders blank rather than "[to be confirmed]".
  const backgroundSummary = htmlToPlainText(job.overviewSummaryHtml ?? "");
  if (backgroundSummary) fields.background_summary = backgroundSummary;

  // Registered office — use structured fields when available, fall back to
  // legacy single-string field for older jobs
  if (job.registeredOfficeStreet) {
    fields.registered_office_street = job.registeredOfficeStreet;
  } else if (job.registeredAddress) {
    fields.registered_office_street = job.registeredAddress;
    const state = deriveLocationState(job.registeredAddress);
    if (state) {
      fields.registered_office_state = state;
      fields.locations_by_state = state;
    }
  }
  if (job.registeredOfficeSuburb)
    fields.registered_office_suburb = job.registeredOfficeSuburb;
  if (job.registeredOfficeState)
    fields.registered_office_state = job.registeredOfficeState;
  if (job.registeredOfficePostcode)
    fields.registered_office_postcode = job.registeredOfficePostcode;

  // Principal place of business
  if (job.principalPlaceStreet)
    fields.principal_place_street = job.principalPlaceStreet;
  if (job.principalPlaceSuburb)
    fields.principal_place_suburb = job.principalPlaceSuburb;
  if (job.principalPlaceState)
    fields.principal_place_state = job.principalPlaceState;
  if (job.principalPlacePostcode)
    fields.principal_place_postcode = job.principalPlacePostcode;

  // Composed single-line "full address" fields for each company address group,
  // so templates can bind one placeholder instead of stitching the parts
  // together. Built from the resolved `fields.*` parts above, and only
  // emitted when at least one part is present. The legacy `registeredAddress`
  // fallback is already a full address string (it may embed its own
  // state/postcode), so it is used as-is rather than re-composed with the
  // separately derived state — composing would duplicate the state, e.g.
  // "123 Example St, Sydney NSW 2000, NSW".
  const registeredOfficeAddress = job.registeredOfficeStreet
    ? composeAddressLine(
        fields.registered_office_street,
        fields.registered_office_suburb,
        fields.registered_office_state,
        fields.registered_office_postcode,
      )
    : fields.registered_office_street;
  if (registeredOfficeAddress) {
    fields.registered_office_address = registeredOfficeAddress;
  }
  const principalPlaceAddress = composeAddressLine(
    fields.principal_place_street,
    fields.principal_place_suburb,
    fields.principal_place_state,
    fields.principal_place_postcode,
  );
  if (principalPlaceAddress) {
    fields.principal_place_address = principalPlaceAddress;
  }

  // MOR-989 — additional places of business. Only document-bound rows are
  // emitted, one formatted address per line, mirroring the name-variant
  // `*_lines` placeholders so templates can list every known premises.
  const additionalPlaceLines = additionalPlaceOfBusinessLines(
    job.additionalPlacesOfBusiness,
  );
  if (additionalPlaceLines) {
    fields.additional_place_of_business_lines = additionalPlaceLines;
  }

  // MOR-429 court / bankruptcy scalars. Preserved verbatim from the job
  // record — do NOT title-case plaintiff/defendant/bankrupt names; those
  // are stored exactly as printed on the order to satisfy statutory
  // citation rules. (Compare with `entityName` above, which IS title-cased
  // for legacy ALL-CAPS rows.)
  if (job.courtName) fields.court_name = job.courtName;
  if (job.courtState) fields.court_state = job.courtState;
  if (job.courtNumber) fields.court_number = job.courtNumber;
  if (job.sequestrationNumber) {
    fields.sequestration_number = job.sequestrationNumber;
  }
  if (job.proceedingMatterNumber) {
    fields.proceeding_matter_number = job.proceedingMatterNumber;
  }
  if (job.proceedingYear) fields.proceeding_year = job.proceedingYear;
  // MOR-772 — the ATO Incap form captures the sequestration year in its own
  // comb boxes. The year is the same value NOA extraction stores as
  // `proceedingYear` (the year portion of the court number); fall back to a
  // 4-digit year embedded in the raw court number for jobs extracted before
  // proceedingYear existed.
  const sequestrationYear =
    job.proceedingYear || job.courtNumber?.match(/(?:19|20)\d{2}/)?.[0];
  if (sequestrationYear) fields.sequestration_year = sequestrationYear;
  if (job.courtOrderDate) fields.court_order_date = job.courtOrderDate;
  if (job.windingUpApplicationDate) {
    fields.winding_up_application_date = job.windingUpApplicationDate;
  }
  if (job.plaintiffName) fields.plaintiff_name = job.plaintiffName;
  if (job.defendantName) fields.defendant_name = job.defendantName;
  if (job.bankruptName) fields.bankrupt_name = job.bankruptName;
  else if (bankruptPersonName) fields.bankrupt_name = bankruptPersonName;
  if (job.bankruptDateOfBirth) {
    fields.bankrupt_date_of_birth = job.bankruptDateOfBirth;
  }
  if (job.administrationNumber) {
    fields.administration_number = job.administrationNumber;
  }
  if (job.petitioningCreditorCosts) {
    fields.petitioning_creditor_costs = job.petitioningCreditorCosts;
  }
  if (job.actOfBankruptcyDate) {
    fields.act_of_bankruptcy_date = job.actOfBankruptcyDate;
  }
  // MOR-901 — the administration / liquidation start dates.
  // `administration_start_date` was already in the canonical field registry but
  // never emitted here, so any template binding it rendered blank.
  if (job.administrationStartDate) {
    fields.administration_start_date = job.administrationStartDate;
  }
  if (job.liquidationDate) fields.liquidation_date = job.liquidationDate;

  // MOR-901 — the appointment this one succeeded, so a liquidator's day-one
  // correspondence can recite the administration it followed.
  const priorAppointment = job.priorAppointments?.length
    ? job.priorAppointments[job.priorAppointments.length - 1]
    : undefined;
  if (priorAppointment) {
    const priorLabel =
      APPOINTMENT_TYPE_TO_INSOLVENCY_TYPE[priorAppointment.appointmentType];
    if (priorLabel) fields.prior_appointment_type = priorLabel;
    if (priorAppointment.appointmentDate) {
      fields.prior_appointment_date = priorAppointment.appointmentDate;
    }
  }

  // MOR-770 — estate/administration bank account entered in Job Settings.
  // `job_bank_details` composes the parts into one block for templates that
  // want a single placeholder (rendered one part per line, same shape as a
  // remittance box on a letter).
  if (job.bankName) fields.job_bank_name = job.bankName;
  if (job.bankAccountName) fields.job_bank_account_name = job.bankAccountName;
  if (job.bankBsb) fields.job_bank_bsb = job.bankBsb;
  if (job.bankAccountNumber) {
    fields.job_bank_account_number = job.bankAccountNumber;
  }
  const bankDetailLines = [
    job.bankName ? `Bank: ${job.bankName}` : null,
    job.bankAccountName ? `Account Name: ${job.bankAccountName}` : null,
    job.bankBsb ? `BSB: ${job.bankBsb}` : null,
    job.bankAccountNumber ? `Account Number: ${job.bankAccountNumber}` : null,
  ].filter(Boolean);
  if (bankDetailLines.length > 0) {
    fields.job_bank_details = bankDetailLines.join("\n");
  }

  // MOR-470 — composed court file reference. Substitutes for the ACN line on
  // personal insolvency (and court liquidation) documents, e.g.
  // "In the Federal Circuit and Family Court of Australia File No: SYG1316/2025".
  // Authors bind a single {{court_file_reference}} placeholder — typically via a
  // corporate-vs-personal variant block — rather than composing the parts.
  const courtFileNumber = job.sequestrationNumber || job.courtNumber;
  if (job.courtName && courtFileNumber) {
    fields.court_file_reference = `In the ${job.courtName} File No: ${courtFileNumber}`;
  } else if (job.courtName) {
    fields.court_file_reference = `In the ${job.courtName}`;
  }

  // Appointment details
  if (job.appointmentDate) fields.appointment_date = job.appointmentDate;
  if (job.appointmentType) {
    const adminType = APPOINTMENT_TYPE_TO_ADMIN_TYPE[job.appointmentType];
    if (adminType) fields.administrator_type = adminType;

    const insolvencyType =
      APPOINTMENT_TYPE_TO_INSOLVENCY_TYPE[job.appointmentType];
    if (insolvencyType) fields.insolvency_type = insolvencyType;

    const method = APPOINTMENT_TYPE_TO_METHOD[job.appointmentType];
    if (method) fields.method_of_appointment = method;
  }

  // Company status based on appointment type
  const statusSuffix: Record<string, string> = {
    cvl: "In Liquidation",
    mvl: "In Liquidation",
    simplified_liquidation: "In Liquidation",
    court_liquidation: "In Liquidation",
    provisional_liquidation: "Provisional Liquidation",
    voluntary_administration: "Administrators Appointed",
    deed_of_company_arrangement: "Subject to Deed of Company Arrangement",
    receivership: "Receivers and Managers Appointed",
    personal_bankruptcy: "Bankrupt Estate",
    business_bankruptcy: "Bankrupt Estate",
    creditors_petition: "Bankrupt Estate",
    debtors_petition: "Bankrupt Estate",
    part_x_arrangement: "Subject to Personal Insolvency Agreement",
  };
  if (job.appointmentType && statusSuffix[job.appointmentType]) {
    fields.company_status = statusSuffix[job.appointmentType];
  }

  // Practitioners — appointees take priority if present
  const appointees = job.appointees?.length ? job.appointees : [];
  const allPractitioners = job.practitioners ?? [];

  // Primary practitioner = first appointee, or first practitioner
  const primary = appointees[0] || allPractitioners[0];
  const secondary = appointees[1] || allPractitioners[1];

  if (primary) {
    fields.practitioner_name = primary;
    fields.signatory_name = primary;
    fields.nominee_1_name = primary;
    // MOR-771 — derive ATO given/family parts for the Incap form. Set each
    // part only when non-empty so a missing middle name stays unmapped rather
    // than rendering a blank mapped value.
    const primaryParts = splitPractitionerName(primary);
    if (primaryParts.firstGivenName)
      fields.practitioner_first_given_name = primaryParts.firstGivenName;
    if (primaryParts.otherGivenNames)
      fields.practitioner_other_given_names = primaryParts.otherGivenNames;
    if (primaryParts.familyName)
      fields.practitioner_family_name = primaryParts.familyName;
  }
  if (secondary) {
    fields.second_practitioner_name = secondary;
    const secondaryParts = splitPractitionerName(secondary);
    if (secondaryParts.firstGivenName)
      fields.second_practitioner_first_given_name =
        secondaryParts.firstGivenName;
    if (secondaryParts.otherGivenNames)
      fields.second_practitioner_other_given_names =
        secondaryParts.otherGivenNames;
    if (secondaryParts.familyName)
      fields.second_practitioner_family_name = secondaryParts.familyName;
  }

  // Practitioner + signatory title from appointment type
  const sigTitle = job.appointmentType
    ? APPOINTMENT_TYPE_TO_ADMIN_TYPE[job.appointmentType]
    : undefined;
  if (sigTitle) {
    fields.practitioner_title = sigTitle;
    fields.signatory_title = sigTitle;
  }

  // MYOB form defaults — practitioners almost always want all three ticked
  fields.myob_add_online_owner = "true";
  fields.myob_remove_admin_password = "true";
  fields.myob_remove_former_users = "true";

  // Auto-assign document dates to today (ISO format YYYY-MM-DD).
  // Sydney "today", not the runtime's UTC day — Convex runs in UTC, so the
  // UTC date is yesterday for the whole Sydney morning and letters came out
  // dated the day before the appointment (MOR-1031).
  const today = sydneyDateIso();
  fields.letter_date = today;
  fields.signature_date = today;
  fields.submission_date = today;

  return fields;
}

function deriveAnzsicDivision(anzsicCode: string): string | undefined {
  const subdivision = Number.parseInt(
    anzsicCode.replace(/\D/g, "").slice(0, 2),
    10,
  );
  if (!Number.isFinite(subdivision)) return undefined;

  const division = ANZSIC_DIVISIONS.find(
    (entry) => subdivision >= entry.start && subdivision <= entry.end,
  );
  return division?.name;
}

/**
 * Compose a single-line address from its parts, e.g.
 * "123 Example St, Sydney NSW 2000". Empty parts are skipped so a partial
 * address renders cleanly without stray commas. Mirrors the comma-joined
 * format used by the report merge-field engine's `composeAddress`.
 */
function composeAddressLine(
  street?: string,
  suburb?: string,
  state?: string,
  postcode?: string,
): string {
  const cityLine = [state, postcode]
    .filter((part) => part && part.trim())
    .join(" ");
  return [street, suburb, cityLine]
    .filter((part) => part && part.trim())
    .join(", ");
}

/**
 * Convert the overview summary's sanitised HTML into plain text for precedent
 * substitution. Block-level closers become paragraph breaks; `<br>` a single
 * newline; remaining tags are dropped and the handful of entities the overview
 * agent can emit (`&amp;`, `&lt;`, `&gt;`, `&nbsp;`, `&quot;`, `&#39;`) are
 * decoded. Dependency-free so this module stays safe to bundle in the browser
 * (unlike `emailParse`, which is `"use node"`).
 */
function htmlToPlainText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|ul|ol|h[1-6]|blockquote)>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function deriveLocationState(address: string): string | undefined {
  const normalized = address.toUpperCase();
  for (const state of AUSTRALIAN_STATE_CODES) {
    if (new RegExp(`\\b${state}\\b`).test(normalized)) return state;
  }
  if (/\bNEW ZEALAND\b|\bNZ\b/.test(normalized)) return "NZ";
  return undefined;
}

// ---------------------------------------------------------------------------
// Contact data → canonical field map (`recipient_*`)
// ---------------------------------------------------------------------------

/**
 * Slim shape consumed by `mapContactToCanonicalFields`. Mirrors the
 * subset of `JobContactRow` (in `src/components/jobs/phases/contacts/types.ts`)
 * that the wizard needs to populate `recipient_*` template fields without
 * coupling this Convex module to the UI's full row type.
 */
export interface ContactDataForFields {
  displayName: string;
  /**
   * Contact type — gates the person-name parts (MOR-643). First/middle/last/
   * full name are only emitted for `"person"` job contacts. Omitted = treated
   * as non-person, so the split parts are skipped.
   */
  contactType?: string | null;
  /** Person-name parts (MOR-643). Unset on legacy rows → derived from displayName. */
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  /** ISO `yyyy-MM-dd`; rendered `dd/MM/yyyy` (job contacts only). */
  dateOfBirth?: string | null;
  abn?: string | null;
  acn?: string | null;
  /**
   * Pre-resolved email — pass the `effectiveEmail` value from
   * `listJobContacts` (job override → firm fallback) so the merged
   * field already reflects the user-visible value.
   */
  email?: string | null;
  /** Pre-resolved phone (same fallback rule as `email`). */
  phone?: string | null;
  addresses?: Array<ContactAddressForFields>;
  /**
   * External administration overlay (job contacts only). A non-empty
   * `externalAdminStatus` on a non-person contact marks the company as being
   * in external administration. The company's own name/address parts are
   * NOT redirected; the appointee is exposed via the explicit
   * `contact_job_external_admin_*` parts. Pass the `externalAdminAppointee`
   * overlay from `listJobContacts`.
   */
  externalAdminStatus?: string | null;
  externalAdminAppointeeTitle?: string | null;
  externalAdminAppointee?: ExternalAdminAppointeeForFields | null;
  /**
   * Resolved primary contact person for a company contact — the record whose
   * name/email/phone fills the `contact_job_primary_contact_*` fields. Pass
   * the `primaryContactPerson` overlay from `listJobContacts`. For a company
   * in external administration with no explicit primary person, the
   * resolver falls back to a person appointee (see
   * `resolvePrimaryContactPerson`).
   */
  primaryContactPerson?: {
    displayName: string;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

/**
 * The appointee of a company in external administration, as resolved by
 * `listJobContacts`. `firm` is the appointee's linked company when the
 * appointee is a person; when the appointee is itself an organisation the
 * resolver treats it as the firm too.
 */
export interface ExternalAdminAppointeeForFields {
  displayName: string;
  contactType?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  addresses?: Array<ContactAddressForFields> | null;
  firm?: {
    displayName: string;
    email?: string | null;
    phone?: string | null;
    addresses?: Array<ContactAddressForFields> | null;
  } | null;
}

/** One postal address on a contact, as stored on `jobContacts.addresses`. */
export interface ContactAddressForFields {
  line1?: string;
  line2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  sourceLabel: string;
}

/**
 * Format a contact address as a single multi-line string suitable for
 * dropping into a letter template's recipient block. Skips empty
 * components so a contact with only line1/state/postcode renders
 * cleanly without blank lines.
 */
function formatContactAddress(address: ContactAddressForFields): string {
  const lines: string[] = [];
  if (address.line1) lines.push(address.line1);
  if (address.line2) lines.push(address.line2);
  const cityLine = [address.suburb, address.state, address.postcode]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(" ");
  if (cityLine) lines.push(cityLine);
  if (address.country && address.country.toUpperCase() !== "AUSTRALIA") {
    lines.push(address.country);
  }
  return lines.join("\n");
}

/** First address with at least one populated component, if any. */
function pickPrimaryAddress(
  addresses: Array<ContactAddressForFields> | null | undefined,
): ContactAddressForFields | undefined {
  return (addresses ?? []).find((a) =>
    Boolean(a.line1 || a.suburb || a.state || a.postcode),
  );
}

/**
 * Trimmed external-administration status, or `undefined` when the contact is
 * not in external administration. Person contacts never are — the overlay is
 * a company concept, so a stray value on a person row is ignored.
 */
function externalAdminStatusOf(
  contact: ContactDataForFields,
): string | undefined {
  if (contact.contactType === "person") return undefined;
  const status = contact.externalAdminStatus?.trim();
  return status ? status : undefined;
}

/**
 * Resolved appointee view for a company in external administration, or
 * `undefined` when the company isn't under administration or has no linked
 * appointee yet.
 *
 * "Firm" is the appointee's linked company when the appointee is a person;
 * an organisation appointee is its own firm. Contact details prefer the
 * appointee's own and fall back to the firm's, so a person row with no
 * email still yields the firm's switchboard. The firm address falls back to
 * the appointee's own address for the same reason.
 */
interface ExternalAdminAppointeeView {
  name: string;
  firmName: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  firmAddress: ContactAddressForFields | undefined;
}

function resolveExternalAdminAppointee(
  contact: ContactDataForFields,
): ExternalAdminAppointeeView | undefined {
  if (!externalAdminStatusOf(contact)) return undefined;
  const appointee = contact.externalAdminAppointee;
  const name = appointee?.displayName.trim();
  if (!appointee || !name) return undefined;

  const isOrganisation =
    appointee.contactType !== undefined &&
    appointee.contactType !== null &&
    appointee.contactType !== "person";
  const firm = appointee.firm ?? (isOrganisation ? appointee : null);

  return {
    name,
    firmName: firm?.displayName.trim() || undefined,
    email: appointee.email?.trim() || firm?.email?.trim() || undefined,
    phone: appointee.phone?.trim() || firm?.phone?.trim() || undefined,
    firmAddress:
      pickPrimaryAddress(firm?.addresses) ??
      pickPrimaryAddress(appointee.addresses),
  };
}

/**
 * The person whose details fill `contact_job_primary_contact_*`.
 *
 * An explicitly linked primary contact person always wins. Otherwise, for a
 * company in external administration whose appointee is a person, the
 * appointee IS the point of contact — the directors no longer speak for the
 * company — so their name and contact details (falling back to their
 * firm's) are used. An organisation appointee is not a "person" and never
 * fills these parts; bind `external_admin_firm` for that.
 */
function resolvePrimaryContactPerson(
  contact: ContactDataForFields,
): NonNullable<ContactDataForFields["primaryContactPerson"]> | undefined {
  if (contact.primaryContactPerson) return contact.primaryContactPerson;
  if (!externalAdminStatusOf(contact)) return undefined;
  const appointee = contact.externalAdminAppointee;
  if (!appointee || appointee.contactType !== "person") return undefined;
  const name = appointee.displayName.trim();
  if (!name) return undefined;
  return {
    displayName: name,
    firstName: appointee.firstName,
    middleName: appointee.middleName,
    lastName: appointee.lastName,
    email: appointee.email?.trim() || appointee.firm?.email?.trim() || null,
    phone: appointee.phone?.trim() || appointee.firm?.phone?.trim() || null,
  };
}

/**
 * Map a `jobContacts` row to the `recipient_*` canonical fields used by
 * letter templates. Only emits keys that have a non-empty value so the
 * three-tier merge (org defaults → job → contact → user overrides)
 * cleanly falls back to template defaults when the contact is missing
 * a phone/email/address.
 *
 * Picks the first non-empty address as the primary recipient address —
 * downstream templates that need every address can iterate
 * `addresses` directly via a dedicated field, but the `recipient_*`
 * keys deliberately collapse to a single block to match how letter
 * templates are written.
 *
 * When `kind` is supplied, the function also mirrors the recipient
 * values into role-specific aliases defined in
 * `convex/settings/precedents/canonicalFields.ts` (e.g. a director
 * contact populates BOTH `recipient_name` AND `director_name`) so
 * existing templates that use role-specific placeholders auto-fill
 * without needing to be re-tagged. Only kinds that have a dedicated
 * canonical-field category emit aliases — for everyone else the
 * `recipient_*` keys are the canonical placeholder.
 *
 * A company in external administration is still addressed as itself here;
 * letters to its appointee bind the explicit `contact_job_external_admin_*`
 * parts from `resolveContactFields` instead.
 */
export function mapContactToCanonicalFields(
  contact: ContactDataForFields,
  kind?: string,
): Record<string, string> {
  const fields: Record<string, string> = {};

  if (contact.displayName) fields.recipient_name = contact.displayName;
  if (contact.email) fields.recipient_email = contact.email;
  if (contact.phone) fields.recipient_phone = contact.phone;
  if (contact.abn) fields.recipient_abn = contact.abn;
  if (contact.acn) fields.recipient_acn = contact.acn;

  const primaryAddress = pickPrimaryAddress(contact.addresses);
  if (primaryAddress) {
    const formatted = formatContactAddress(primaryAddress);
    if (formatted) fields.recipient_address = formatted;
    if (primaryAddress.line1)
      fields.recipient_address_line1 = primaryAddress.line1;
    if (primaryAddress.line2)
      fields.recipient_address_line2 = primaryAddress.line2;
    if (primaryAddress.suburb)
      fields.recipient_address_suburb = primaryAddress.suburb;
    if (primaryAddress.state)
      fields.recipient_address_state = primaryAddress.state;
    if (primaryAddress.postcode) {
      fields.recipient_address_postcode = primaryAddress.postcode;
    }
  }

  // Role-specific aliases — keep keys in sync with `FIELD_CATEGORIES` in
  // `convex/settings/precedents/canonicalFields.ts`. Currently only the
  // "director" category exposes role-specific placeholder keys; all
  // other contact roles flow through `recipient_*`.
  const isDirectorRole =
    kind === "director" || kind === "former_director" || kind === "corporate"; // legacy source-kind compat
  if (isDirectorRole) {
    if (fields.recipient_name) fields.director_name = fields.recipient_name;
    if (fields.recipient_email) fields.director_email = fields.recipient_email;
    if (fields.recipient_address)
      fields.director_address = fields.recipient_address;
  }

  return fields;
}

/**
 * Resolve a contact into the explicit `contact:<domain>:<part>`
 * field-value map consumed by the "Job Contact" / "Firm Contact"
 * template field sources.
 *
 * Unlike `mapContactToCanonicalFields` (which emits the legacy implicit
 * `recipient_*` keys), every key here is namespaced by the exact binding
 * the template author chose — so a placeholder bound to
 * `contact:job:email` is filled from this contact's email and nothing
 * else. Only parts with a non-empty value are emitted, so a chip bound
 * to a missing part renders empty rather than stale.
 *
 * `domain` distinguishes a per-job contact (`job`) from a firm-directory
 * contact (`firm`) — both share the same physical shape, so one resolver
 * handles either. WHICH contact this is called for is decided by the
 * fan-out (one call per matching tagged contact).
 */
export function resolveContactFields(
  contact: ContactDataForFields,
  domain: ContactFieldDomain,
): Record<string, string> {
  const fields: Record<string, string> = {};
  const set = (part: string, value: string | null | undefined) => {
    if (!value) return;
    fields[buildContactFieldKey({ domain, part: part as never })] = value;
  };

  set("name", contact.displayName);
  set("email", contact.email);
  set("phone", contact.phone);
  set("abn", contact.abn);
  set("acn", contact.acn);

  // Person-name parts (MOR-643) — job domain, person contacts only. "Name"
  // (above) is the canonical First + Middle + Last (MOR-976); here we add the
  // individually-bindable parts plus "Full Name" (also First + Middle + Last).
  // Legacy rows with no stored parts derive First/Last from `displayName` via
  // `deriveNameParts`. `set` skips empties, so a missing Middle renders blank
  // rather than stale.
  if (domain === "job" && contact.contactType === "person") {
    const { firstName, middleName, lastName } = deriveNameParts(contact);
    set("first_name", firstName);
    set("middle_name", middleName);
    set("last_name", lastName);
    set("full_name", composeFullName(firstName, middleName, lastName));
  }

  // Date of birth is a person/job-oriented part — scoped to the job domain
  // via `domains: ["job"]` in CONTACT_FIELD_PARTS. MOR-562.
  if (domain === "job" && contact.dateOfBirth) {
    set("date_of_birth", formatDate(contact.dateOfBirth));
  }

  const primaryAddress = pickPrimaryAddress(contact.addresses);
  if (primaryAddress) {
    set("address_full", formatContactAddress(primaryAddress));
    set("address_line1", primaryAddress.line1);
    set("address_line2", primaryAddress.line2);
    set("address_suburb", primaryAddress.suburb);
    set("address_state", primaryAddress.state);
    set("address_postcode", primaryAddress.postcode);
  }

  // External administration overlay + primary contact person — job domain
  // only (both live on job contacts; the firm directory carries neither).
  if (domain === "job") {
    const status = externalAdminStatusOf(contact);
    set("external_admin_status", status);
    set(
      "name_with_status",
      status ? `${contact.displayName} (${status})` : contact.displayName,
    );
    const appointee = resolveExternalAdminAppointee(contact);
    if (appointee) {
      set("external_admin_appointee", appointee.name);
      set(
        "external_admin_appointee_title",
        contact.externalAdminAppointeeTitle?.trim(),
      );
      set("external_admin_appointee_email", appointee.email);
      set("external_admin_appointee_phone", appointee.phone);
      set("external_admin_firm", appointee.firmName);
      if (appointee.firmAddress) {
        set(
          "external_admin_firm_address",
          formatContactAddress(appointee.firmAddress),
        );
      }
    }

    const primary = resolvePrimaryContactPerson(contact);
    if (primary) {
      set("primary_contact_name", primary.displayName);
      const {
        firstName: pFirst,
        middleName: pMiddle,
        lastName: pLast,
      } = deriveNameParts({
        displayName: primary.displayName,
        firstName: primary.firstName ?? undefined,
        middleName: primary.middleName ?? undefined,
        lastName: primary.lastName ?? undefined,
      });
      set("primary_contact_first_name", pFirst);
      set("primary_contact_middle_name", pMiddle);
      set("primary_contact_last_name", pLast);
      set("primary_contact_full_name", composeFullName(pFirst, pMiddle, pLast));
      set("primary_contact_email", primary.email);
      set("primary_contact_phone", primary.phone);
    }
  }

  return fields;
}

/**
 * Slim definition shape consumed by `resolveContactCustomFields`. Mirrors
 * the subset of a `firmContactFieldDefinitions` row the resolver needs —
 * decoupled from the Convex `Doc` type so this module stays portable.
 */
export interface ContactCustomFieldDef {
  key: string;
  fieldType: FirmContactCustomFieldType;
}

/** Formats a trimmed custom-field value per its definition's type. */
function formatCustomFieldValue(
  fieldType: FirmContactCustomFieldType,
  trimmed: string,
): string | undefined {
  switch (fieldType) {
    case "currency":
      return formatCurrency(parseCurrencyInput(trimmed));
    case "date":
      return formatDate(trimmed);
    default:
      return trimmed;
  }
}

/**
 * Resolve a contact's raw `customFields` map into the explicit
 * `contact_<domain>_cf_<key>` field-value map consumed by the precedent
 * renderer.
 *
 * Iterates the org's field DEFINITIONS (not the stored keys) so that:
 *  - only currently-defined fields render (orphaned values are dropped),
 *  - the correct formatter is applied even when a definition's type
 *    changed after the value was entered.
 *
 * Values are pre-formatted by type — currency as `$X.XX`, dates as
 * `dd/MM/yyyy` — mirroring `resolveContactSourceFields` so output is
 * render-ready. Empty / unparseable values are dropped so the placeholder
 * falls back to a blank rather than emitting `NaN`/`undefined`.
 */
export function resolveContactCustomFields(
  customFields: Record<string, string> | undefined,
  definitions: ReadonlyArray<ContactCustomFieldDef>,
  domain: ContactFieldDomain,
): Record<string, string> {
  const fields: Record<string, string> = {};
  if (!customFields) return fields;

  for (const def of definitions) {
    const raw = customFields[def.key];
    if (raw === undefined || raw === null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;

    const formatted = formatCustomFieldValue(def.fieldType, trimmed);
    if (!formatted) continue;
    fields[buildContactCustomFieldKey({ domain, key: def.key })] = formatted;
  }

  return fields;
}

/**
 * Resolve a runtime-PICKED firm contact into the `firmref_<category>_*`
 * field-value map consumed by firm-contact *references* (the dual of the
 * `contact_firm_*` fan-out family).
 *
 * Same value extraction and type formatting as `resolveContactFields` +
 * `resolveContactCustomFields`, but every key is written under the
 * `firmref_<category>_*` codec so it binds to the single picked contact rather
 * than to a fan-out recipient. `definitions` should already be filtered to the
 * given `category` (custom-field keys are unique per `(org, category)`), and
 * only parts/customs with a non-empty value are emitted so a missing reference
 * renders blank rather than stale.
 */
export function resolveFirmReferenceFields(
  category: FirmContactCategory,
  contact: ContactDataForFields,
  customFields: Record<string, string> | undefined,
  definitions: ReadonlyArray<ContactCustomFieldDef>,
): Record<string, string> {
  const fields: Record<string, string> = {};

  const setBasic = (part: string, value: string | null | undefined) => {
    if (!value) return;
    fields[buildFirmRefFieldKey({ category, part: part as never })] = value;
  };

  setBasic("name", contact.displayName);
  setBasic("email", contact.email);
  setBasic("phone", contact.phone);
  setBasic("abn", contact.abn);
  setBasic("acn", contact.acn);

  const primaryAddress = (contact.addresses ?? []).find((a) =>
    Boolean(a.line1 || a.suburb || a.state || a.postcode),
  );
  if (primaryAddress) {
    setBasic("address_full", formatContactAddress(primaryAddress));
    setBasic("address_line1", primaryAddress.line1);
    setBasic("address_line2", primaryAddress.line2);
    setBasic("address_suburb", primaryAddress.suburb);
    setBasic("address_state", primaryAddress.state);
    setBasic("address_postcode", primaryAddress.postcode);
  }

  for (const def of definitions) {
    const raw = customFields?.[def.key];
    if (raw === undefined || raw === null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;

    const formatted = formatCustomFieldValue(def.fieldType, trimmed);
    if (!formatted) continue;
    fields[buildFirmRefCustomFieldKey({ category, key: def.key })] = formatted;
  }

  return fields;
}

/** All bindable contact-field parts, re-exported for convenience. */
export { CONTACT_FIELD_PARTS };

// ---------------------------------------------------------------------------
// Contact source-data fields (`contact_<domain>_<kind>_<attr>`)
// ---------------------------------------------------------------------------

/**
 * Format an AUD amount as a render-ready currency string.
 * Mirrors the precedent renderer's currency formatting so values dropped
 * into a letter look identical whether they came from a custom field or
 * from a source row. Returns `undefined` for non-finite numbers so the
 * resolver can drop the key (rather than emit "NaN") and let the
 * template fall back to a blank.
 */
/**
 * Parse a user-entered currency string into a number. Users type formatted
 * values like "30,000", "$30,000.50", or "30 000" — strip the grouping
 * punctuation and currency symbol before parsing so those don't collapse
 * to NaN and get silently dropped.
 */
function parseCurrencyInput(raw: string): number {
  return Number(raw.replace(/[$,\s]/g, ""));
}

function formatCurrency(value: number | undefined | null): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format an ISO-ish date string as `dd/MM/yyyy` for letter copy.
 * Accepts the inconsistent date shapes the agents emit (ISO with or
 * without time, already-formatted dd/MM/yyyy strings, etc.) — anything
 * we can't parse is returned verbatim so the practitioner can see the
 * raw value rather than a silent drop.
 */
function formatDate(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  // Already dd/MM/yyyy — leave as-is so we don't reformat agent output
  // that was specifically picked by the data layer.
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  const dd = String(parsed.getUTCDate()).padStart(2, "0");
  const mm = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = String(parsed.getUTCFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Format a boolean as a localised Yes/No string. Used for fields like
 * `liability.secured` / `corporate.is_current` where the template
 * author wants a human-readable answer rather than `true`/`false`.
 */
function formatYesNo(value: boolean | undefined | null): string | undefined {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return undefined;
}

/**
 * Format a snake_case enum value as a Title Case label. Used for
 * direction / agreement-status / collateral-type / etc. so a value
 * like `proof_of_debt` reads as "Proof Of Debt" in a letter. Returns
 * `undefined` when the input is empty so the key falls through to a
 * blank rather than emitting "Undefined".
 */
function formatEnum(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed
    .split(/[_\s]+/u)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Pick a renderable string from an asset row. Asset valuations live in
 * an embedded `valuations[]` array on `assetEntries`, with a separate
 * `selectedValuation.valuationIndex` pointer for the practitioner's
 * preferred figure. Falls back to the first valuation when nothing has
 * been selected so the field still renders for jobs that haven't
 * curated the asset.
 */
function pickAssetValuation(row: {
  valuations?: unknown;
  selectedValuation?: unknown;
}): { value?: number; asAtDate?: string } | undefined {
  const valuations = Array.isArray(row.valuations) ? row.valuations : [];
  if (valuations.length === 0) return undefined;
  const selected = (row.selectedValuation ?? null) as {
    valuationIndex?: number;
  } | null;
  const idx =
    selected && typeof selected.valuationIndex === "number"
      ? selected.valuationIndex
      : 0;
  const valuation = valuations[idx] ?? valuations[0];
  if (!valuation || typeof valuation !== "object") return undefined;
  const v = valuation as { value?: unknown; asAtDate?: unknown };
  return {
    value: typeof v.value === "number" ? v.value : undefined,
    asAtDate: typeof v.asAtDate === "string" ? v.asAtDate : undefined,
  };
}

/**
 * Resolve a single agent source row into the `contact_<domain>_<kind>_<part>`
 * map consumed by the precedent renderer.
 *
 * The shape of `row` differs per `sourceKind` — this resolver is the
 * only place that knows about the agent-table column names, mirroring
 * the per-kind branches in `convex/contacts/queries.ts:fetchSourceRow`.
 * Only fields with a non-empty resolved value are emitted so missing
 * data renders as a blank placeholder rather than `undefined`.
 *
 * Currency / date / boolean / enum values are pre-formatted here so the
 * template output is render-ready (e.g. `$250,000.00` and `12/05/2026`
 * rather than raw numbers and ISO strings). This mirrors the
 * formatting used elsewhere in the precedent pipeline.
 *
 * Pair with `getJobContactSourceRows` (the query) and
 * `CONTACT_SOURCE_FIELDS` (the registry) — every `part` listed in the
 * registry MUST have a corresponding read here (and vice versa).
 */
export function resolveContactSourceFields(
  sourceKind: ContactSourceKind,
  row: unknown,
  domain: ContactFieldDomain = "job",
  /**
   * The contact's `sourceLinks[].sourceId` for this row. Joint secured
   * parties share one securityInterestEntries row via suffixed link ids
   * (`<rowId>#joint:<normalised name>`); passing the link id lets the
   * secured-party fields resolve to THAT entity instead of always the
   * lead party (MOR-708). Omit for row-level resolution (lead party).
   */
  linkSourceId?: string,
): Record<string, string> {
  const fields: Record<string, string> = {};
  if (!row || typeof row !== "object") return fields;
  const set = (part: string, value: string | undefined | null) => {
    if (value === undefined || value === null) return;
    const trimmed = String(value).trim();
    if (!trimmed) return;
    fields[buildContactSourceFieldKey({ domain, sourceKind, part })] = trimmed;
  };

  switch (sourceKind) {
    case "security_interest": {
      const r = row as {
        ppsrRegistrationNumber?: string;
        givingOfNoticeIdentifier?: string;
        collateralDescription?: string;
        collateralClass?: string;
        collateralType?: string;
        registrationStartDate?: string;
        registrationEndDate?: string;
        agreementDate?: string;
        registrationStatus?: string;
        fixedOrFloating?: string;
        pmsi?: boolean;
        owedAmount?: number;
        owedAmountAsAt?: string;
        securedParty?: {
          name?: string;
          identifier?: string;
          identifierType?: string;
          addresses?: Array<{
            line1: string;
            suburb: string;
            state: string;
            postcode: string;
            type: string;
          }>;
        };
        jointSecuredParties?: Array<{
          name?: string;
          identifier?: string;
          identifierType?: string;
          addresses?: Array<{
            line1: string;
            suburb: string;
            state: string;
            postcode: string;
            type: string;
          }>;
        }>;
      };

      // Joint registrations: when the link id names a joint party
      // (`#joint:<normalised name>`), the secured-party fields must
      // describe THAT entity — not the row's lead party. The suffix was
      // built with `normaliseName`, so match with the same function.
      const jointMarker = linkSourceId?.indexOf("#joint:") ?? -1;
      const jointParty =
        linkSourceId && jointMarker !== -1
          ? (() => {
              const wanted = linkSourceId.slice(jointMarker + "#joint:".length);
              return (r.jointSecuredParties ?? []).find(
                (p) => normaliseName(p.name ?? "") === wanted,
              );
            })()
          : undefined;
      const party = jointParty ?? r.securedParty;

      set("ppsr_number", r.ppsrRegistrationNumber);
      set("collateral_description", r.collateralDescription);
      set("collateral_class", formatEnum(r.collateralClass));
      set("collateral_type", formatEnum(r.collateralType));
      set("registration_date", formatDate(r.registrationStartDate));
      set("registration_end_date", formatDate(r.registrationEndDate));
      set("agreement_date", formatDate(r.agreementDate));
      set("registration_status", formatEnum(r.registrationStatus));
      set("fixed_or_floating", formatEnum(r.fixedOrFloating));
      set("pmsi", formatYesNo(r.pmsi));
      set("owed_amount", formatCurrency(r.owedAmount));
      set("owed_amount_as_at", formatDate(r.owedAmountAsAt));
      set("secured_party_name", party?.name);
      // The "Notice Identifier" quoted on secured-party correspondence is
      // the registration's PPSR "Giving of Notice Identifier". Only when a
      // registration has no dedicated identifier does PPSR fall back to
      // identifying the secured party by its ACN/ABN.
      const goni = r.givingOfNoticeIdentifier?.trim();
      set("secured_party_identifier", goni || party?.identifier);
      set(
        "secured_party_identifier_type",
        goni ? "Giving of Notice Identifier" : party?.identifierType,
      );
      // Joint parties usually share the registration's address for
      // service — fall back to the lead party's address when the joint
      // party carries none of its own.
      const addr = party?.addresses?.[0] ?? r.securedParty?.addresses?.[0];
      if (addr) {
        set(
          "secured_party_address",
          [addr.line1, addr.suburb, addr.state, addr.postcode]
            .filter(Boolean)
            .join(", "),
        );
      }
      break;
    }
    case "liability": {
      const r = row as {
        amount?: number;
        natureOfDebt?: string;
        date?: string;
        secured?: boolean;
        securityDetails?: string;
      };
      set("amount", formatCurrency(r.amount));
      set("nature", formatEnum(r.natureOfDebt));
      set("date", formatDate(r.date));
      set("secured", formatYesNo(r.secured));
      set("security_details", r.securityDetails);
      break;
    }
    case "loan": {
      const r = row as {
        amount?: number;
        outstandingBalance?: number;
        agreementStatus?: string;
        interestRateAnnual?: number;
        repaymentTerms?: string;
        securityStatus?: string;
        date?: string;
      };
      set("amount", formatCurrency(r.amount));
      set("outstanding_balance", formatCurrency(r.outstandingBalance));
      set("agreement_status", formatEnum(r.agreementStatus));
      // Annual interest rate is stored as a decimal fraction (0.075) on
      // the agent side. Render as a percentage with one decimal so a
      // letter reads "7.5%" rather than "0.075".
      if (
        typeof r.interestRateAnnual === "number" &&
        Number.isFinite(r.interestRateAnnual)
      ) {
        const pct = (r.interestRateAnnual * 100)
          .toFixed(2)
          .replace(/\.?0+$/u, "");
        set("interest_rate", `${pct}%`);
      }
      set("repayment_terms", formatEnum(r.repaymentTerms));
      set("security_status", formatEnum(r.securityStatus));
      set("date", formatDate(r.date));
      break;
    }
    case "employee": {
      const r = row as {
        role?: string;
        employmentCategory?: string;
        entitlements?: EmployeeEntitlementAmounts;
        startDate?: string;
        terminationDate?: string;
        residencyStatus?: string;
      };
      set("role", r.role);
      set("employment_category", formatEnum(r.employmentCategory));
      const ent = r.entitlements ?? {};
      set("total_entitlements", formatCurrency(sumEmployeeEntitlements(ent)));
      set("feg_recoverable_total", formatCurrency(sumFegRecoverable(ent)));
      set("wages_owed", formatCurrency(ent.unpaidWages));
      set("super_owed", formatCurrency(ent.unpaidSuper));
      set("annual_leave_owed", formatCurrency(ent.accruedAnnualLeave));
      set("leave_loading_owed", formatCurrency(ent.leaveLoading));
      set(
        "long_service_leave_owed",
        formatCurrency(ent.accruedLongServiceLeave),
      );
      set("payment_in_lieu_owed", formatCurrency(ent.paymentInLieuOfNotice));
      set("redundancy_owed", formatCurrency(ent.redundancyPay));
      set("hire_date", formatDate(r.startDate));
      set("termination_date", formatDate(r.terminationDate));
      set("residency_status", formatEnum(r.residencyStatus));
      break;
    }
    case "corporate": {
      const r = row as {
        category?: string;
        entityType?: string;
      };
      set("category", formatEnum(r.category));
      set("entity_type", formatEnum(r.entityType));
      break;
    }
    case "related_party": {
      const r = row as {
        partyType?: string;
        relationshipCategory?: string;
        relationshipStatus?: string;
        basisOfRelationship?: string[];
      };
      set("party_type", formatEnum(r.partyType));
      set("relationship_category", formatEnum(r.relationshipCategory));
      set("relationship_status", formatEnum(r.relationshipStatus));
      if (
        Array.isArray(r.basisOfRelationship) &&
        r.basisOfRelationship.length > 0
      ) {
        set("basis_of_relationship", r.basisOfRelationship.join(", "));
      }
      break;
    }
    case "asset_valuation": {
      const r = row as {
        description?: string;
        assetType?: string;
        valuations?: unknown;
        selectedValuation?: unknown;
      };
      set("asset_description", r.description);
      set("asset_type", formatEnum(r.assetType));
      const valuation = pickAssetValuation(r);
      if (valuation) {
        set("valuation_value", formatCurrency(valuation.value));
        set("valuation_as_at", formatDate(valuation.asAtDate));
      }
      break;
    }
    case "ato_sro": {
      const r = row as {
        authority?: string;
        authorityLabel?: string;
        runningBalance?: { amount?: number; asAtDate?: string };
      };
      set("authority", r.authority);
      set("authority_label", r.authorityLabel);
      if (r.runningBalance && typeof r.runningBalance === "object") {
        set("running_balance", formatCurrency(r.runningBalance.amount));
        set("running_balance_as_at", formatDate(r.runningBalance.asAtDate));
      }
      break;
    }
  }

  return fields;
}

/**
 * Canonical keys that `mapContactToCanonicalFields` can produce. Used
 * by the wizard / preview UI to colour-code field chips by source (so
 * practitioners can see "this came from the contact" vs "this came
 * from the job").
 */
export const CONTACT_DERIVABLE_FIELD_KEYS: ReadonlySet<string> = new Set([
  "recipient_name",
  "recipient_email",
  "recipient_phone",
  "recipient_abn",
  "recipient_acn",
  "recipient_address",
  "recipient_address_line1",
  "recipient_address_line2",
  "recipient_address_suburb",
  "recipient_address_state",
  "recipient_address_postcode",
  // Role-specific aliases populated by `mapContactToCanonicalFields`
  // when a role is supplied. Mirror entries in `FIELD_CATEGORIES`.
  "director_name",
  "director_email",
  "director_address",
]);

/** Options for `mergeFieldValues`. */
export interface MergeFieldValuesOptions {
  /**
   * The job's appointment type. Selects which appointment-type-scoped title
   * overrides on the Practitioner preset apply (MOR-2354). Without it no
   * house-style title applies and the job-derived title stands.
   */
  appointmentType?: string | null;
}

/**
 * Field merge in priority order.
 *
 * Layered low-to-high so later sources overwrite earlier ones:
 *   1. orgDefaults — preset / branding-level firm values (title configuration
 *      keys removed: they are settings, not document fields)
 *   2. jobFields — derived from the job record (company name, ACN, etc.)
 *   2b. scoped title overrides — the Practitioner preset's
 *      `signatory_title:<type>` / `practitioner_title:<type>` values for the
 *      job's OWN appointment type (MOR-2354), over the job-derived title.
 *      A bankruptcy "Trustee" never reaches a liquidation; a legacy unscoped
 *      title value is never applied.
 *   3. contactFields — `recipient_*` derived from the linked contact
 *      (only present for contact-role fan-out generation)
 *   4. userOverrides — per-document edits from the wizard
 *
 * Post-merge fallbacks (only when the target key is ABSENT — an explicit
 * empty string is a deliberate blank the analyst chose, and stays blank;
 * MOR-2342 QA-08):
 * - nominee_1_email ← practitioner_email (Gallagher notification nominee — so
 *   changing the lead appointee propagates to Gallagher)
 * - contact_name    ← practitioner_name  (day-to-day contact defaults to the
 *   appointee when the firm hasn't configured a dedicated contact preset)
 * - contact_phone   ← firm_phone
 * - contact_email   ← firm_email
 *
 * `contactFields` is optional so existing call-sites that don't yet
 * have a contact in scope (every non-`cr:*` template, plus all the
 * legacy "regenerate" / "edit fields" flows) keep working unchanged.
 */
export function mergeFieldValues(
  jobFields: Record<string, string>,
  orgDefaults: Record<string, string>,
  userOverrides?: Record<string, string>,
  contactFields?: Record<string, string>,
  options?: MergeFieldValuesOptions,
): Record<string, string> {
  const merged: Record<string, string> = {
    ...withoutTitleConfigKeys(orgDefaults),
    ...jobFields,
    ...resolveScopedTitles(orgDefaults, options?.appointmentType),
    ...(contactFields ?? {}),
    ...userOverrides,
  };

  if (merged.nominee_1_email === undefined && merged.practitioner_email) {
    merged.nominee_1_email = merged.practitioner_email;
  }

  if (merged.contact_name === undefined && merged.practitioner_name) {
    merged.contact_name = merged.practitioner_name;
  }
  if (merged.contact_phone === undefined && merged.firm_phone) {
    merged.contact_phone = merged.firm_phone;
  }
  if (merged.contact_email === undefined && merged.firm_email) {
    merged.contact_email = merged.firm_email;
  }

  return merged;
}

/**
 * Canonical keys that `mapJobToCanonicalFields` can produce.
 *
 * Keep in sync with the function above. Used by the template library preview
 * (`PreviewDialog`) to colour-code field chips by source so practitioners can
 * see at a glance whether a placeholder will be auto-filled from the job
 * record, an org preset, a custom firm field, the template's own example
 * value, or requires manual entry per document.
 *
 * Order matches declaration order in `mapJobToCanonicalFields` — no semantic
 * ranking implied.
 */
export const JOB_DERIVABLE_FIELD_KEYS: ReadonlySet<string> = new Set([
  "job_code",
  "company_name",
  // MOR-2342 — personal-insolvency naming: the explicit estate heading and the
  // insurer-facing appointment name (person on a bankruptcy, company otherwise).
  "bankrupt_estate_name",
  "appointment_name",
  "company_name_variant_lines",
  "trading_name_lines",
  "trust_name_lines",
  "former_trading_name_lines",
  "acn",
  "abn",
  // MOR-340 — trust handling. `trust_abn` is the most likely insertion in
  // precedents (operating-business identifier when the company acts as
  // trustee). `trust_name` and `trustee_capacity` round out the set.
  "trust_abn",
  "trust_name",
  "trustee_capacity",
  "anzsic_code",
  "industry_division",
  "business_industry",
  "background_summary",
  "registered_office_street",
  "registered_office_suburb",
  "registered_office_state",
  "registered_office_postcode",
  "registered_office_address",
  "principal_place_street",
  "principal_place_suburb",
  "principal_place_state",
  "principal_place_postcode",
  "principal_place_address",
  "additional_place_of_business_lines",
  "locations_by_state",
  "appointment_date",
  "administrator_type",
  "insolvency_type",
  "method_of_appointment",
  "company_status",
  // MOR-901 appointment history + administration/liquidation start dates
  "prior_appointment_type",
  "prior_appointment_date",
  "administration_start_date",
  "liquidation_date",
  "practitioner_name",
  "practitioner_first_given_name",
  "practitioner_other_given_names",
  "practitioner_family_name",
  "signatory_name",
  "nominee_1_name",
  "second_practitioner_name",
  "second_practitioner_first_given_name",
  "second_practitioner_other_given_names",
  "second_practitioner_family_name",
  "practitioner_title",
  "signatory_title",
  "myob_add_online_owner",
  "myob_remove_admin_password",
  "myob_remove_former_users",
  "letter_date",
  "signature_date",
  "submission_date",
  // MOR-429 day-one fields
  "court_name",
  "court_state",
  "court_number",
  "sequestration_number",
  // MOR-772 — year portion of the sequestration/court number
  "sequestration_year",
  "proceeding_matter_number",
  "proceeding_year",
  "court_order_date",
  "winding_up_application_date",
  "plaintiff_name",
  "defendant_name",
  "bankrupt_name",
  "administration_number",
  "petitioning_creditor_costs",
  "act_of_bankruptcy_date",
  // MOR-470 — composed court file reference (ACN-line substitute)
  "court_file_reference",
  // MOR-770 — job bank account (Job Settings → Job Details)
  "job_bank_name",
  "job_bank_account_name",
  "job_bank_bsb",
  "job_bank_account_number",
  "job_bank_details",
]);
